import type {
  AmountPercent,
  BedType,
  CancellationPolicy,
  Deadline,
  DepositPolicy,
  GuaranteePolicy,
  Occupancy,
  RoomRate as RoomRateInput,
} from '@elara/domain/models/schemas/room-schemas';
import type { SabreDeadline } from '@elara/domain/models/schemas/sabre-schemas';
import type { CancellationDeadline } from '@elara/domain/payment-schedule';

import type { MealCombination, MealCombinationId, RoomRateModel } from '#models/RoomRateModel.js';
import type { IconName } from '@elara/client-core/ui-contracts/icon-name';

import { format, isToday, isValid, parseISO, subDays } from 'date-fns';

import { formatPrice, pluralize } from './utils.js';

import { isFullAmountDeposit } from '@elara/domain/payment-schedule';

// Pure deposit-policy rule now lives in @elara/domain (web + native import identical code).
export { isFullAmountDeposit } from '@elara/domain/payment-schedule';

const CANCEL_DATE_FMT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

/**
 * "Free cancellation until Mar 12, 2026" / "Non-refundable" from a resolved structured deadline
 * (`resolveCancellationDeadline` in `@elara/domain/payment-schedule`). Shared by web and mobile so the
 * headline cancellation fact reads identically wherever a room's rate is shown.
 */
export function formatCancellationDeadline(d: CancellationDeadline | undefined): string | undefined {
  if (!d) return undefined;
  if (!d.refundable) return 'Non-refundable';
  if (!d.freeUntil) return 'Free cancellation';
  return d.freeUntil.getTime() < Date.now()
    ? `Free-cancellation deadline passed (${CANCEL_DATE_FMT.format(d.freeUntil)})`
    : `Free cancellation until ${CANCEL_DATE_FMT.format(d.freeUntil)}`;
}

/** Why a room search picked a room (`RoomRate.recommendation.tag`). */
export type RoomPickTag = NonNullable<RoomRateInput['recommendation']>['tag'];

/**
 * What a picked room's badge says, or undefined for no badge: the curation agent's label for this
 * trip ("Most space", "Best for kids") when it wrote one, else the tag's own words — except
 * `suggested`, which says nothing every presented room does not (they are all Elara’s), and also
 * marks the rooms the search added to fill the set. Shared so web and mobile badges read the same.
 */
export function roomPickLabel(recommendation: { tag: RoomPickTag; label?: string }): string | undefined {
  const label = recommendation.label?.trim();
  if (label) return label;
  return recommendation.tag === 'suggested' ? undefined : ROOM_PICK_TAG_LABELS[recommendation.tag];
}

/**
 * A picked room's badge in the tag's own words (ELARA-1092), when the curation agent wrote no label of its
 * own. `suggested` has none: every presented room is Elara's pick (see `roomPickLabel`).
 */
export const ROOM_PICK_TAG_LABELS: Record<Exclude<RoomPickTag, 'suggested'>, string> = {
  lowest: 'Lowest price',
  flexible: 'Most flexible',
  best_value: 'Best value',
  best_room: 'Best room',
};

/** The most rooms of one rate the room count picker adds; the add flow accepts no more. */
export const ROOM_QUANTITY_MAX = 5;

/**
 * The metadata a shortlist button saves with a room: the party the room was searched for, so the saved
 * card keeps pricing that party even after a later search moves the room's own `search.occupancy`.
 * No rate: the app's buttons save the room unpinned and its card leads with the lowest bookable rate
 * (`RoomOptionModel.leadRateFor`). Undefined when the search recorded no party (Sabre rooms): the card
 * then prices whichever party the room's latest search asked about.
 */
export function roomShortlistMetadata(room: {
  search?: { occupancy?: Occupancy };
}): { occupancy: Occupancy } | undefined {
  const occupancy = room.search?.occupancy;
  return occupancy ? { occupancy } : undefined;
}

// Map RMA codes to Material Icon names
// Based on official OTA Room Amenity Type codes: https://modelviewers.pilotfishtechnology.com/modelviewers/OTA/model/codelists/ota?term=RMA
const ROOM_AMENITY_ICON_MAP: Record<number, IconName> = {
  // Room configuration
  1: 'door_sliding', // Adjoining rooms
  22: 'door_sliding', // Connecting rooms
  185: 'family_restroom', // Family/oversized room
  206: 'favorite', // Honeymoon suites

  // Time & alarms
  119: 'schedule', // Wake-up calls

  // Baby & child amenities
  6: 'baby_changing_station', // Baby listening device
  26: 'crib', // Cribs
  91: 'bed', // Rollaway bed
  252: 'bed', // Child rollaway
  279: 'baby_changing_station', // Children's playpen

  // Bathroom amenities
  9: 'hot_tub', // Bath tub with spray jets
  13: 'bathtub', // Bath tub only
  14: 'bathtub', // Bathtub only
  15: 'bathtub', // Bathtub/shower combination
  97: 'shower', // Shower only
  127: 'bathtub', // Bathtub & whirlpool separate
  141: 'bathtub', // Oversized bathtub
  142: 'shower', // Shower only
  155: 'bathroom', // Separate tub and shower
  244: 'hot_tub', // Hinoki tub

  // Coffee & tea
  19: 'coffee_maker', // Coffee/Tea maker

  // Office & work
  28: 'desk', // Desk

  // Kitchen & dining
  68: 'microwave', // Microwave

  // Comfort & furniture
  102: 'weekend', // Sofa bed
  200: 'bed', // Futon

  // Bedding & linens
  186: 'bed', // Hypoallergenic bed
  187: 'bed', // Hypoallergenic pillows

  // Room features
  144: 'volume_off', // Soundproofed room
  41: 'fireplace', // Fireplace

  // Smoking
  74: 'smoke_free', // Non-smoking
  101: 'smoking_rooms', // Smoking

  // Pets
  140: 'pets', // Pets allowed

  // Accessibility
  161: 'accessible', // Accessible room
  221: 'accessible', // Accessible room (duplicate code)

  // Religious
  83: 'prayer_times', // Prayer mats

  // Other amenities
  267: 'lock', // No adult channels or adult channel lock
  268: 'air', // Non-allergenic room
  264: 'dark_mode', // Blackout curtain
};

export const ROOM_AMENITY_SHORTLIST = Object.keys(ROOM_AMENITY_ICON_MAP).map(Number);

/**
 * A room's beds as one line, "1 King Bed, 1 Sofa Bed", so a room with several kinds of bed reads as one fact
 * (cards and guides show it once, with the first bed's icon). Undefined when the room lists none.
 */
export function formatBedTypes(bedTypes: readonly Pick<BedType, 'count' | 'description'>[]): string | undefined {
  if (bedTypes.length === 0) return;
  return bedTypes.map((bed) => `${bed.count} ${pluralize(bed.count, bed.description)}`).join(', ');
}

/**
 * Bed icon by Sabre bed-type code. Travelport rooms carry no Sabre code (beds are named), so
 * `undefined` is a normal input and renders the generic bed.
 */
export function getBedTypeIcon(code: number | undefined): IconName {
  if (code === 58 || code === 86) {
    return 'king_bed';
  }
  if (code === 203) {
    return 'single_bed';
  }
  return 'bed';
}

export function getRoomAmenityIcon(code: number): IconName {
  return ROOM_AMENITY_ICON_MAP[code] ?? 'check';
}

/**
 * Formats the room size string by formatting the first number with commas
 * @param size - Room size string like "290 sq ft (83 sq m)" or "900 sq ft"
 * @returns Formatted string with comma-separated numbers
 */
export function formatRoomSize(size: string): string {
  // Match the first number in the string
  const match = size.match(/^(\d+(?:\.\d+)?)\s*(.*)$/);
  if (!match || !match[1]) return size;

  const numberStr = match[1];
  const rest = match[2] ?? '';
  const number = Number.parseFloat(numberStr);
  if (Number.isNaN(number)) return size;

  // Format with commas and 0 decimal places
  const formattedNumber = number.toLocaleString('en-US', { maximumFractionDigits: 0 });
  return `${formattedNumber} ${rest}`;
}

/**
 * Filters rates by refundability preference.
 */
export function filterRatesByRefundable(rates: RoomRateModel[], wantsRefundable: boolean): RoomRateModel[] {
  return rates.filter((rate) => (wantsRefundable ? rate.isRefundable : rate.isRefundable === false));
}

/**
 * Filters rates by meal combination id.
 */
export function filterRatesByMealCombination(
  rates: RoomRateModel[],
  combinationId: MealCombinationId,
): RoomRateModel[] {
  return rates.filter((rate) => rate.matchesMealCombination(combinationId));
}

/**
 * Normalizes optional boolean flags to strict booleans.
 */
export function normalizeBoolean(value: boolean | undefined): boolean {
  return value === true;
}

/**
 * Checks if a set of meal flags matches a meal combination.
 */
export function matchesMealCombination(
  flags: { breakfastIncluded?: boolean; lunchIncluded?: boolean; dinnerIncluded?: boolean },
  combination: MealCombination,
) {
  const breakfastMatch = combination.includes.breakfast
    ? flags.breakfastIncluded === true
    : flags.breakfastIncluded !== true;
  const lunchMatch = combination.includes.lunch ? flags.lunchIncluded === true : flags.lunchIncluded !== true;
  const dinnerMatch = combination.includes.dinner ? flags.dinnerIncluded === true : flags.dinnerIncluded !== true;
  return breakfastMatch && lunchMatch && dinnerMatch;
}

/**
 * Derives the meal combination id for a given rate using provided combinations.
 */
export function deriveMealCombinationId(rate: RoomRateInput, combinations: MealCombination[]): MealCombinationId {
  const matching = combinations.find((combination) => matchesMealCombination(rate, combination));
  return matching?.id ?? 'none';
}

/**
 * Formats an absolute deadline string to a human-readable format
 * Returns "today" if the deadline is today, otherwise formats the date
 */
/**
 * Short timezone label for a date in the runtime's local zone (e.g. "EDT", "GMT+8"). On the client this
 * is the GUEST's timezone — so a Singapore guest sees their own local deadline, never the hotel's clock.
 * Surfaced explicitly so the clock the deadline refers to is never ambiguous (ELARA-829).
 */
function tzSuffix(date: Date): string {
  try {
    const label = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
      .formatToParts(date)
      .find((p) => p.type === 'timeZoneName')?.value;
    return label ? ` (${label})` : '';
  } catch {
    return '';
  }
}

function formatAbsoluteDeadline(deadline: string): string {
  // Try parseISO first (handles ISO 8601 with/without timezone)
  let parsed: Date | undefined;
  try {
    parsed = parseISO(deadline);
    if (isValid(parsed)) {
      // Check if the deadline is today
      if (isToday(parsed)) {
        return 'today';
      }
      // Check if it includes time information
      const hasTime = deadline.includes('T') || /\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(deadline);
      if (hasTime) {
        return `by ${format(parsed, "h:mm a 'on' MMMM d, yyyy")}${tzSuffix(parsed)}`;
      }
      return `by ${format(parsed, 'MMMM d, yyyy')}`;
    }
  } catch {
    // parseISO failed, try native Date parsing
  }

  // Fallback to native Date constructor (handles many formats)
  const nativeParsed = new Date(deadline);
  if (isValid(nativeParsed) && !Number.isNaN(nativeParsed.getTime())) {
    // Check if the deadline is today
    if (isToday(nativeParsed)) {
      return 'today';
    }
    const hasTime = deadline.includes(':');
    if (hasTime) {
      return `by ${format(nativeParsed, "h:mm a  'on' MMMM d, yyyy")}${tzSuffix(nativeParsed)}`;
    }
    return `by ${format(nativeParsed, 'MMMM d, yyyy')}`;
  }

  // If all parsing fails, return as-is (might already be formatted)
  return deadline;
}

/**
 * Formats a relative deadline to a human-readable string
 */
function formatRelativeDeadline(
  multiplier: number,
  unit: string,
  dropTime: SabreDeadline['OffsetDropTime'],
  checkInDate?: string,
): string | undefined {
  // Format the time unit
  let unitStr: string;
  switch (unit) {
    case 'D':
    case 'Day':
    case 'Days': {
      unitStr = multiplier === 1 ? 'day' : 'days';

      break;
    }
    case 'H':
    case 'Hour':
    case 'Hours': {
      unitStr = multiplier === 1 ? 'hour' : 'hours';

      break;
    }
    case 'M':
    case 'Minute':
    case 'Minutes': {
      unitStr = multiplier === 1 ? 'minute' : 'minutes';

      break;
    }
    default: {
      // Unknown unit, return undefined
      return;
    }
  }

  // Format the drop time reference
  let dropTimeStr: string;
  switch (dropTime) {
    case 'BeforeArrival': {
      dropTimeStr = 'before arrival';
      break;
    }
    case 'AfterBooking': {
      dropTimeStr = 'after booking';
      break;
    }
    case 'AfterConfirmation': {
      dropTimeStr = 'after confirmation';
      break;
    }
    case 'AfterArrival': {
      dropTimeStr = 'after arrival';
      break;
    }
    case 'AfterDeparture': {
      dropTimeStr = 'after departure';
      break;
    }
    default: {
      return;
    }
  }

  // When we know the arrival date and the offset is "before arrival", show the CONCRETE date (check-in
  // minus the offset). Day-grained only — Sabre gives no time-of-day on these, so we never invent a clock.
  if (checkInDate && dropTime === 'BeforeArrival' && /^(D|Day|Days)$/.test(unit)) {
    const checkIn = parseISO(checkInDate);
    if (isValid(checkIn)) {
      return `${multiplier} ${unitStr} before check-in — ${format(subDays(checkIn, multiplier), 'MMM d, yyyy')}`;
    }
  }
  return `${multiplier} ${unitStr} ${dropTimeStr}`;
}

/**
 * Formats a deadline (absolute or relative) to a human-readable string
 */
function formatDeadline(deadline?: Deadline, checkInDate?: string): string | undefined {
  if (!deadline) return;

  // Absolute deadline takes precedence
  if (deadline.absoluteDeadline) {
    return `Cancel ${formatAbsoluteDeadline(deadline.absoluteDeadline)}.`;
  }

  // Relative deadline
  if (deadline.offsetDropTime && deadline.offsetUnitMultiplier && deadline.offsetTimeUnit) {
    const timeStr = formatRelativeDeadline(
      deadline.offsetUnitMultiplier,
      deadline.offsetTimeUnit,
      deadline.offsetDropTime,
      checkInDate,
    );
    // "Before arrival" offsets are a property-side rule with no time-of-day — frame them as hotel local time.
    return timeStr ? `Cancel up to ${timeStr} (hotel local time).` : undefined;
  }

  return;
}

/**
 * Formats a penalty amount to a human-readable string
 */
function formatPenalty(amountPercent?: AmountPercent, isRefundable?: boolean): string | undefined {
  if (!amountPercent) {
    // If refundable and no penalty specified, it's free cancellation
    if (isRefundable) return undefined;
    return;
  }

  // The hotel's RULE first — a percent of the stay or a number of nights — because that is what
  // the hotel will actually charge. A money figure is shown only when it is the stated fee, or as
  // "about" when the supplier marked it as its own estimate for this stay (Travelport's search
  // marks nearly every amount that way, and its one-night estimates were measured wrong on
  // Marriott brands on 2026-09-28).
  if (amountPercent.percent === 100) {
    return '100% of the stay.';
  }
  if (amountPercent.percent) {
    return `${amountPercent.percent}% of the stay.`;
  }
  if (amountPercent.numberOfNights) {
    const n = amountPercent.numberOfNights;
    return n === 1 ? "One night's charge." : `${n} nights' charge.`;
  }

  if (amountPercent.amount && amountPercent.currency) {
    const numericAmount = amountPercent.amount;
    if (!Number.isNaN(numericAmount)) {
      const formatted = formatPrice(numericAmount, amountPercent.currency);
      return amountPercent.estimated ? `About ${formatted}.` : `${formatted} fee.`;
    }
  }

  if (amountPercent.numberOfNights) {
    return `${amountPercent.numberOfNights} night${amountPercent.numberOfNights > 1 ? 's' : ''} charge.`;
  }

  return;
}

/**
 * Formats a deposit deadline part (without "deposit due" prefix) for combining with amount
 */
function formatDepositDeadlinePart(deadline?: Deadline): string | undefined {
  if (!deadline) return;

  // Absolute deadline takes precedence
  if (deadline.absoluteDeadline) {
    return formatAbsoluteDeadline(deadline.absoluteDeadline);
  }

  // Relative deadline
  if (deadline.offsetDropTime && deadline.offsetUnitMultiplier && deadline.offsetTimeUnit) {
    const timeStr = formatRelativeDeadline(
      deadline.offsetUnitMultiplier,
      deadline.offsetTimeUnit,
      deadline.offsetDropTime,
    );
    if (timeStr) {
      return timeStr;
    }
  }

  return;
}

/**
 * Formats a deposit amount part (without "deposit" suffix) for combining with deadline
 */
function formatDepositAmountPart(amountPercent?: AmountPercent): string | undefined {
  if (!amountPercent) return;

  // Format based on what's available
  if (amountPercent.percent) {
    return `${amountPercent.percent}%`;
  }

  if (amountPercent.amount && amountPercent.currency) {
    const numericAmount = amountPercent.amount;
    if (!Number.isNaN(numericAmount)) {
      return formatPrice(numericAmount, amountPercent.currency);
    }
  }

  if (amountPercent.numberOfNights) {
    return `${amountPercent.numberOfNights} night${amountPercent.numberOfNights > 1 ? 's' : ''}`;
  }

  return;
}

/**
 * Summarizes guarantee policies from Sabre guarantee accepted items
 */
export function summarizeGuaranteePolicy(guaranteePolicy?: GuaranteePolicy): string | undefined {
  if (!guaranteePolicy?.guaranteesAccepted?.length) return undefined;

  const descriptions = guaranteePolicy.guaranteesAccepted
    .map((accepted) => accepted.description)
    .filter(Boolean)
    .map((desc) => desc!.toLowerCase());

  if (descriptions.length === 0) return undefined;

  // Check if any guarantee type is credit card and if AMEX is accepted
  const hasCreditCardGuarantee = descriptions.some((desc) => desc.includes('credit card'));
  let amexAccepted = false;

  if (hasCreditCardGuarantee) {
    // Check all payment cards across all guarantee accepted items
    amexAccepted = guaranteePolicy.guaranteesAccepted.some((accepted) =>
      accepted.paymentCards?.some((card) => card.name === 'American Express'),
    );
  }

  // Keep descriptions lowercase
  let guaranteeOption: string;
  if (descriptions.length === 1) {
    guaranteeOption = descriptions[0]!;
  } else if (descriptions.length === 2) {
    guaranteeOption = `${descriptions[0]} or ${descriptions[1]}`;
  } else {
    // For 3+ items, use Oxford comma: "a, b, or c"
    const allButLast = descriptions.slice(0, -1).join(', ');
    const last = descriptions.at(-1);
    guaranteeOption = `${allButLast}, or ${last}`;
  }

  let policy = `Guaranteed with ${guaranteeOption}`;

  // Add AMEX not accepted note if credit card guarantee is required but AMEX is not accepted
  if (hasCreditCardGuarantee && !amexAccepted) {
    policy += ' (AMEX not accepted)';
  }

  return policy;
}

/**
 * The deposit lines a rate card lists: the generated summary when the rate has one, else its structured
 * policy summarized, and none when the deposit is the whole stay (the price already says that). Without a
 * closing full stop, like the card's other lines ("Breakfast included", "Free cancellation until …").
 */
export function rateDepositLines(
  rate: Pick<RoomRateModel, 'generated' | 'depositPolicies' | 'localPricing'>,
): string[] {
  const generated = rate.generated?.depositPolicy;
  const lines =
    generated != null
      ? [generated]
      : rate.depositPolicies &&
          !isFullAmountDeposit(rate.depositPolicies, rate.localPricing.totalPrice, rate.localPricing.currency)
        ? summarizeDepositPolicy(rate.depositPolicies)
        : [];
  // One closing full stop only: an ellipsis is part of the line.
  return lines.map((line) => line.trim().replace(/(?<!\.)\.$/, ''));
}

/**
 * Summarizes deposit policies from Sabre response
 */
export function summarizeDepositPolicy(depositPolicies?: DepositPolicy[]): string[] {
  if (!depositPolicies?.length) return [];

  const policies = [...depositPolicies];

  return policies
    .map((dp) => {
      const amountPart = formatDepositAmountPart(dp.amountPercent);
      const deadlinePart = formatDepositDeadlinePart(dp.deadline);

      // A deposit the supplier marked as a portion of the total: say the balance follows.
      const partial = dp.partial ? ', balance due later' : '';

      // Combine amount and deadline: "{amount} deposit due {deadline}"
      if (amountPart && deadlinePart) {
        return `${amountPart} deposit due ${deadlinePart}${partial}`;
      }

      // If only amount, return "{amount} deposit"
      if (amountPart) {
        return `${amountPart} deposit${partial}`;
      }

      // If only deadline, return "Deposit due {deadline}"
      if (deadlinePart) {
        return `Deposit due ${deadlinePart}${partial}`;
      }

      return;
    })
    .filter(Boolean) as string[];
}

/**
 * Summarizes cancellation policies from Sabre response
 */
export function summarizeCancelPolicy(cancelPolicy?: CancellationPolicy[], checkInDate?: string): string[] {
  if (!cancelPolicy?.length) return [];

  const penalties = [...cancelPolicy];

  return penalties
    .map((cp) => {
      const parts: string[] = [];

      // Format deadline
      const deadlineStr = formatDeadline(cp.deadline, checkInDate);
      if (deadlineStr) {
        parts.push(deadlineStr);
      }

      // Format penalty amount
      const penaltyStr = formatPenalty(cp.amountPercent, cp.refundable);
      if (penaltyStr) {
        parts.push(penaltyStr);
      }

      return parts.join(' ');
    })
    .filter(Boolean);
}

/**
 * The BINDING cancellation-policy description for a rate or a booking, built off the deterministic
 * structured terms (`summarizeCancelPolicy`) first — the legally-defensible deadline + penalty facts —
 * falling back to the room-enrichment agent's natural-language prose only when no structured terms
 * exist, then a bare refundable/non-refundable statement. The generated prose must never be the
 * primary/binding text: an LLM can soften or omit a penalty, which is exactly the misrepresentation
 * risk this ordering avoids (ELARA-829 R1). Shared by hotel checkout (`policy-utils.ts`) and every
 * post-booking surface (`ItineraryItemModel.cancellationPolicyText`) so they can't drift apart.
 */
export function describeCancellationPolicyFacts(
  facts: { cancellationPolicies?: CancellationPolicy[]; generatedCancellationPolicy?: string; refundable?: boolean },
  checkInDate?: string,
): string | undefined {
  const summarized = summarizeCancelPolicy(facts.cancellationPolicies, checkInDate);
  if (summarized.length > 0) return summarized.join(' · ');
  const generated = facts.generatedCancellationPolicy?.trim();
  if (generated) return generated;
  if (facts.refundable === true) return 'Refundable — see rate terms.';
  if (facts.refundable === false) return 'Non-refundable.';
  return undefined;
}
